import { ChatMembersList } from 'components/organisms'
import * as moment from 'moment'
import * as React from 'react'
import 'react-dates/initialize'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../core/i18n'
import { IJsonResponse } from '../../../core/JsonApiSerializer'
import { directReservationService, reservationService } from '../../../core/services'
import * as interfaces from '../../../static/interfaces'
import { Button } from '../../atoms'
import MessageLayout from '../_layouts/messageLayout'
import { Channels, Chat } from './parts'
import ConversationGuide from './parts/ConversationGuide'
import GuideListModal from './parts/GuideListModal'
import SearchFormForGuideAssign, { IGuideSearchParams } from './parts/SearchFormForGuideAssign'

declare var window: interfaces.IWindow

interface IProps {
  user: IJsonResponse
  reservation: IJsonResponse
  agent: IJsonResponse
  guide_list: IJsonResponse
  assign_guides: IJsonResponse
  for_direct_reservation: boolean
  guide_calendars: IJsonResponse | null
  skills: any
  tour_skills: any
  areas: any
}

export type BlockedTimeZone =
  | {
      am: boolean
      pm: boolean
    }
  | null
  | undefined

const formatUserData = userData => {
  return {
    ...userData,
    included: userData.included.map(data => ({
      ...data,
      type: data.attributes.agent ? 'agent' : 'guide',
    })),
  }
}
const isPassedReservation = ({ end_at }) => {
  const date = I18n.locale === 'en' ? moment(end_at) : moment(end_at, 'YYYY年MM月DD日 HH:mm')

  return date.isBefore()
}

const GuideAssigns: React.FC<IProps> = props => {
  const service = props.for_direct_reservation ? directReservationService : reservationService
  const { data: user } = service.getDataFromJson(props.user)
  const { data: parsedReservation } = service.getDataFromJson(props.reservation)
  const [reservation, setReservation] = React.useState<any>(parsedReservation)
  const [guide, setGuide] = React.useState<interfaces.IUser>(null)
  const [agent, setAgent] = React.useState<interfaces.IUser>(null)
  const [guideList, setGuideList] = React.useState([])

  const [assignedGuide, setAssignedGuide] = React.useState(null)
  const [messageData, setMessageData] = React.useState<{
    messages: interfaces.IMessage[]
    conversationId: number
  }>({
    messages: [],
    conversationId: 0,
  })

  const startAt = moment(reservation.start_at).format('YYYY-MM-DD')
  const initialSearchParam = {
    fullName: '',
    day: startAt,
    date: reservation.start_at,
    selectedSkills: null,
    selectedTourSkills: null,
    selectedAreas: null,
    selectedAvailability: null,
  }
  const [guideSearchParam, setGuideSearchParam] = React.useState<IGuideSearchParams>(
    initialSearchParam
  )

  const currentSerchParam = React.useRef(guideSearchParam)

  React.useEffect(() => {
    currentSerchParam.current = guideSearchParam
  }, [guideSearchParam])

  const currentGuideList = React.useRef(guideList)

  React.useEffect(() => {
    currentGuideList.current = guideList
  }, [guideList])

  const getFormattedParams = param => {
    return {
      fullname: param.fullname,
      day: param.day,
      skill_ids: param.selectedSkills?.map(skill => Number(skill.value)) || [],
      tour_skill_ids: param.selectedTourSkills?.map(tourSkill => Number(tourSkill.value)) || [],
      area_ids: param.selectedAreas?.map(tourSkill => Number(tourSkill.value)) || [],
      time_ids: param.selectedAvailability?.map(availability => Number(availability.value)) || [],
    }
  }

  const getFormattedSearchOption = (param: IGuideSearchParams, page?: number) => {
    const searchParam = getFormattedParams(param)
    setGuideSearchParam(param)

    if (searchParam.day === '') {
      searchParam.day = initialSearchParam.day
    }
    return page ? { page, searchParam } : { searchParam }
  }

  const updateGuideList = async (targetGuide, onFinish) => {
    const already = currentGuideList.current.find(({ guide_id }) => guide_id === targetGuide.id)

    if (already === undefined) {
      const { conversationId } = await service.createGuideConversation({
        id: reservation.id,
        params: {
          guide_assign: {
            guide_id: targetGuide.id,
          },
        },
      })
      const newGuide = {
        conversation_id: conversationId,
        guide_id: targetGuide.id,
        guide: targetGuide,
      }

      setGuideList(old => [...old, newGuide])
      setGuide(targetGuide)
    } else {
      setGuide(targetGuide)
    }
    onFinish()
  }

  const getUpdatedModalBody = async (param?: IGuideSearchParams, page?: number) => {
    const { guides, pagination, guide_calendars } = await service.getReservationGuides(
      reservation.id,
      getFormattedSearchOption(param, page)
    )

    return (
      <GuideListModal
        newGuides={guides}
        newPagination={pagination}
        guideList={guideList}
        guideCalendars={guide_calendars}
        reservationID={reservation.id}
        day={param.day}
        for_direct_reservation={props.for_direct_reservation}
        updateGuideList={updateGuideList}
        onChangePageHandler={onChangePageHandler}
      />
    )
  }

  const showGuideListlModal = async (param: IGuideSearchParams) => {
    const ModalBody = await getUpdatedModalBody(param)

    window.globalModal.showModal({
      title: I18n.t('reservation.select_conversation_guide'),
      body: ModalBody,
      closeText: I18n.t('generic.close'),
    })
  }

  const onChangePageHandler = async (page?: number) => {
    const ModalBody = await getUpdatedModalBody(guideSearchParam, page)

    window.globalModal.updateModalBody(ModalBody)
  }

  const createMessageOnAssignOrUnAssign = async (message, conversation) => {
    const formData = new FormData()

    formData.append('message[body]', message)
    formData.append('message[sender_id]', user.id)
    formData.append('message[conversation_id]', conversation.conversation_id)
    formData.append('message[reservation_id]', reservation.id)

    await reservationService.createMessage(formData)
  }

  const assignGuide = async () => {
    const { flush } = await service.reservationGuideAssign({
      id: reservation.id,
      params: {
        guide_assign: {
          guide_id: guide.id,
        },
      },
    })
    setAssignedGuide(guide)
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })

    const conversation = guideList.find(
      conversationguide => conversationguide.guide_id === guide.id
    )

    createMessageOnAssignOrUnAssign(
      I18n.t('reservation.assigned_guide', { name: guide.username }),
      conversation
    )
  }

  const unassignGuide = async () => {
    const { flush } = await service.reservationGuideUnassign({
      id: reservation.id,
      params: {
        guide_assign: {
          guide_id: assignedGuide.id,
        },
      },
    })

    setAssignedGuide(null)
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })

    // メッセージログを残す
    const conversation = guideList.find(
      conversationguide => conversationguide.guide_id === assignedGuide.id
    )

    createMessageOnAssignOrUnAssign(
      I18n.t('reservation.unassigned_guide', { name: guide.username }),
      conversation
    )
  }

  const handleDeleteDirectReservation = async () => {
    if (window.confirm(I18n.t('generic.delete_confirmation'))) {
      deleteDirectReservation(reservation)
      window.location.href = '/agent/host_reservations'
    }
  }

  const deleteDirectReservation = async directReservation => {
    const { flush } = await directReservationService.deleteDirectReservation(directReservation.id)

    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
  }

  const renderCurrentWorkFlowStateButtons = React.useCallback(() => {
    if (
      isPassedReservation(reservation) ||
      !user.agent ||
      (!props.for_direct_reservation && !reservation.workflow_state)
    ) {
      return null
    }

    return (
      <>
        {assignedGuide ? (
          <Button small={true} handleClick={unassignGuide} disabled={!guide}>
            {I18n.t('reservation.unassign_guide')}
          </Button>
        ) : (
          <Button small={true} handleClick={assignGuide} disabled={!guide}>
            {I18n.t('reservation.assign_guide')}
          </Button>
        )}
        {props.for_direct_reservation && (
          <Button handleClick={handleDeleteDirectReservation}>{I18n.t('generic.delete')}</Button>
        )}
      </>
    )
  }, [reservation, assignedGuide, guide, user])

  // チャットを取得する
  const getMessages = async () => {
    const conversation = guideList.find(
      conversationguide => conversationguide.guide_id === guide.id
    )
    if (conversation !== undefined) {
      const { data: newMessages } = await service.getReservationMessages({
        conversation_id: conversation.conversation_id,
      })
      const { data: parsedMessages } = service.getDataFromJson(newMessages)
      setMessageData({
        messages: parsedMessages,
        conversationId: conversation.conversation_id,
      })
    }
  }

  React.useEffect(() => {
    if (guide) {
      getMessages()
    }
  }, [guide])

  // Didmount
  React.useEffect(() => {
    if (user.guide) {
      setGuide(user)
    }

    if (user.agent) {
      setAgent(user)
    } else {
      const { data: parsedAgent } = service.getDataFromJson(props.agent)
      setAgent(parsedAgent)
    }

    if (props.guide_list) {
      const { data: list } = service.getDataFromJson(formatUserData(props.guide_list))
      setGuideList(list)
    }

    if (props.assign_guides) {
      const { data: parsedGuide } = service.getDataFromJson(props.assign_guides)
      setAssignedGuide(parsedGuide[0])
      if (!user.guide) {
        setGuide(parsedGuide[0])
      }
    }
  }, [])

  return (
    <MessageLayout
      user={user}
      host={agent}
      receipt_id={null}
      reservation={reservation}
      setReservation={setReservation}
      pageName="assign_guide"
      Main={
        <GuideAssignMain>
          {user.agent &&
            reservation.workflow_state !== 'Canceled' &&
            reservation.workflow_state !== 'Declined' &&
            !isPassedReservation(reservation) && (
              <SearchFormForGuideAssign
                startAt={reservation.start_at}
                areas={props.areas}
                skills={props.skills}
                tour_skills={props.tour_skills}
                handleSearch={values => showGuideListlModal(values)}
              />
            )}
          {user.agent && (
            <S.GuideLists>
              <h3>{I18n.t('reservation.select_conversation_guide')}</h3>
              <div className="GuideListWrapper">
                {guideList.map(guideData => (
                  <ConversationGuide
                    key={guideData.guide.id}
                    guide={guideData.guide}
                    selectGuide={setGuide}
                    isSelected={guide?.id === guideData.guide.id}
                    isAssigned={assignedGuide?.id === guideData.guide.id}
                  />
                ))}
              </div>
            </S.GuideLists>
          )}
          {guide && messageData.conversationId > 0 && (
            <Chat
              user={user}
              initMessages={messageData.messages}
              conversationId={messageData.conversationId}
            />
          )}
        </GuideAssignMain>
      }
      Side={
        <>
          {renderCurrentWorkFlowStateButtons()}
          <Channels page="assign_guide" user={user} reservation={parsedReservation} />
          <ChatMembersList guide={guide} guests={[null]} agent={agent} />
        </>
      }
    />
  )
}

const S: { [key: string]: AnyStyledComponent } = {}
const GuideAssignMain = styled.div`
  display: flex;
  flex-direction: column;
`
S.GuideLists = styled.div`
  width: 100%;
  .GuideListWrapper {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-top: 20px;

    &::after {
      content: '';
      display: block;
      width: calc(30% - 25px);
      height: 0;
      min-width: 220px;
      max-width: 380px;
    }
  }
`

export default GuideAssigns
